/**
 * Arabic Language Profile
 *
 * VSO word order, prepositions, RTL (right-to-left), space-separated.
 * Features root-based morphology and rich verb conjugation.
 */

import type { LanguageProfile } from './types';

export const arabicProfile: LanguageProfile = {
  code: 'ar',
  name: 'Arabic',
  nativeName: 'العربية',
  regions: ['priority'],
  direction: 'rtl',
  script: 'arabic',
  wordOrder: 'VSO',
  markingStrategy: 'preposition',
  usesSpaces: true,
  verb: {
    position: 'start',
    subjectDrop: true,
  },
  references: {
    me: 'أنا', // "I/me" - first person
    it: 'هو', // "it" (masculine)
    you: 'أنت', // "you"
    result: 'النتيجة',
    event: 'الحدث',
    target: 'الهدف',
    body: 'جسم', // matches the i18n dict's emitted body word (corpus-canonical, parser must recognize it)
    document: 'وثيقة',
    window: 'نافذة',
    detail: 'تفاصيل',
  },
  possessive: {
    marker: '', // No explicit marker - uses possessive pronouns
    markerPosition: 'after-object',
    usePossessiveAdjectives: true,
    specialForms: {
      // Arabic: "value لي" (value for-me) - possessive pronoun follows property
      me: 'لي', // "for me" / "mine"
      it: 'له', // "for it" / "its"
      you: 'لك', // "for you" / "yours"
    },
    keywords: {
      // "my" / "mine"
      لي: 'me',
      // "your" / "yours"
      لك: 'you',
      // "its/his/her"
      له: 'it', // his/its (masculine)
      لها: 'it', // her/its (feminine)
    },
  },
  roleMarkers: {
    destination: {
      primary: 'على',
      alternatives: ['في', 'إلى', 'ب', 'قبل', 'بعد'],
      position: 'before',
    },
    source: { primary: 'من', position: 'before' },
    patient: { primary: '', position: 'before' },
    style: { primary: 'بـ', alternatives: ['باستخدام'], position: 'before' },
  },
  keywords: {
    // Class/Attribute operations
    toggle: { primary: 'بدّل', alternatives: ['بدل', 'غيّر', 'غير'], normalized: 'toggle' },
    add: { primary: 'أضف', alternatives: ['اضف'], normalized: 'add' },
    remove: { primary: 'احذف', alternatives: ['أزل', 'امسح'], normalized: 'remove' },
    // Content operations
    put: { primary: 'ضع', alternatives: ['اجعل'], normalized: 'put' },
    append: { primary: 'ألحق', normalized: 'append' },
    prepend: { primary: 'سبق', normalized: 'prepend' },
    take: { primary: 'خذ', normalized: 'take' }, // Removed احصل to avoid collision with get
    make: { primary: 'اصنع', alternatives: ['أنشئ'], normalized: 'make' },
    clone: { primary: 'استنسخ', normalized: 'clone' },
    swap: { primary: 'استبدل', alternatives: ['تبادل'], normalized: 'swap' },
    morph: { primary: 'حوّل', normalized: 'morph' },
    // Variable operations
    set: { primary: 'اضبط', alternatives: ['عيّن', 'حدد'], normalized: 'set' },
    get: { primary: 'احصل', normalized: 'get' }, // Removed خذ to avoid collision with take
    increment: { primary: 'زِد', alternatives: ['زد', 'ارفع'], normalized: 'increment' },
    decrement: { primary: 'أنقص', alternatives: ['انقص', 'قلل'], normalized: 'decrement' },
    log: { primary: 'سجل', normalized: 'log' },
    // Visibility
    show: { primary: 'اظهر', alternatives: ['أظهر', 'اعرض'], normalized: 'show' },
    hide: { primary: 'اخف', alternatives: ['أخفِ', 'اخفي', 'أخف'], normalized: 'hide' }, // Added hamza variant
    transition: { primary: 'انتقال', alternatives: ['انتقل'], normalized: 'transition' },
    // Events
    on: { primary: 'على', alternatives: ['عند', 'لدى', 'حين'], normalized: 'on' },
    trigger: { primary: 'تشغيل', alternatives: ['أطلق', 'فعّل'], normalized: 'trigger' },
    send: { primary: 'أرسل', normalized: 'send' },
    // DOM focus
    focus: { primary: 'تركيز', alternatives: ['ركز'], normalized: 'focus' },
    blur: { primary: 'ضبابية', alternatives: ['شوش'], normalized: 'blur' },
    // Phase 1 (v0.9.90): DOM / form state / debug
    empty: { primary: 'إفراغ', alternatives: ['فارغ'], normalized: 'empty' },
    open: { primary: 'افتح', normalized: 'open' },
    close: { primary: 'أغلق', normalized: 'close' },
    select: { primary: 'ظلل', normalized: 'select' },
    clear: { primary: 'نظّف', normalized: 'clear' },
    reset: { primary: 'إعادة-ضبط', alternatives: ['أعد-تعيين'], normalized: 'reset' },
    breakpoint: { primary: 'نقطة-توقف', normalized: 'breakpoint' },
    // Navigation
    go: { primary: 'اذهب', normalized: 'go' },
    scroll: { primary: 'مرر', alternatives: ['تمرير'], normalized: 'scroll' },
    push: { primary: 'ادفع', alternatives: ['دفع'], normalized: 'push' },
    replace: { primary: 'استبدل_عنوان', alternatives: ['أعد_تعيين_عنوان'], normalized: 'replace' },
    process: { primary: 'عالج', alternatives: ['معالجة'], normalized: 'process' },
    // Async
    wait: { primary: 'انتظر', normalized: 'wait' },
    fetch: { primary: 'احضر', alternatives: ['جلب'], normalized: 'fetch' },
    settle: { primary: 'استقر', normalized: 'settle' },
    // Control flow
    if: { primary: 'إذا', normalized: 'if' },
    unless: { primary: 'إلا', normalized: 'unless' },
    when: { primary: 'عندما', normalized: 'when' },
    where: { primary: 'أين', normalized: 'where' },
    else: { primary: 'وإلا', alternatives: ['خلاف ذلك'], normalized: 'else' },
    repeat: { primary: 'كرر', normalized: 'repeat' },
    for: { primary: 'لكل', normalized: 'for' },
    while: { primary: 'بينما', normalized: 'while' },
    // `repeat forever` loop keyword — corpus word recognized so loopType types
    // as `:literal` like EN (the repeat.loopType R1 residue; see spanish.ts).
    forever: { primary: 'forever', normalized: 'forever' },
    continue: { primary: 'واصل', normalized: 'continue' },
    halt: { primary: 'أوقف', normalized: 'halt' },
    throw: { primary: 'ارم', alternatives: ['ارمِ'], normalized: 'throw' },
    call: { primary: 'استدع', alternatives: ['نادِ'], normalized: 'call' },
    return: { primary: 'ارجع', alternatives: ['عُد'], normalized: 'return' },
    then: { primary: 'ثم', alternatives: ['بعدها', 'ثمّ'], normalized: 'then' },
    and: { primary: 'وأيضاً', alternatives: ['أيضاً'], normalized: 'and' },
    // Comparison operator (`target matches .x`). Deferred by the Phase 2 `matches`
    // slice because ar's operand ALSO leaked (`references.target` carried الهدف while
    // the dict emits هدف), and registering the operator without its operand is worse
    // than neither: modal-close-backdrop ar passed R2 only BY ACCIDENT — the unparsed
    // condition was dropped, so `hide` ran unconditionally and coincidentally matched
    // the en DOM effect. `matches` alone would parse the condition into a real
    // comparison whose operand هدف evaluates to undefined, stopping `hide` and
    // flipping R2 pass→fail at tolerance 0. Landing WITH the هدف EXTRAS entry
    // (arabic.ts tokenizer) renders `target matches .modal-backdrop`, byte-identical
    // to en. Not an ActionType and has no command schema, so no pattern is generated.
    matches: { primary: 'يطابق', normalized: 'matches' },
    // Existence operator (`if #modal exists`). Same seam as `matches`: without the
    // keyword the surface stays an identifier and leaks verbatim into the
    // condition's raw expression (if-exists). Neither an ActionType nor a command
    // schema, so no pattern is generated from it.
    exists: { primary: 'موجود', normalized: 'exists' },
    // Negative-existence operator (`if no dragHandle set dragHandle to me`). Same
    // seam as `exists`: without the keyword the surface stays an identifier and
    // leaks verbatim into the condition's raw expression (behavior-draggable).
    // Neither an ActionType nor a command schema, so no pattern is generated from it.
    // Uses the dict's NATURAL spaced phrase `لا يوجد`, matched by the base
    // tokenizer's multi-word keyword walk (longest-phrase at a word boundary) —
    // the same mechanism hi `मेل खाता` uses. Does not collide with `not: 'ليس'`.
    no: { primary: 'لا يوجد', normalized: 'no' },
    // آخر is deliberately ABSENT: it is the positional `last` keyword
    // (آخر <button/> في .modal — see pattern-matcher's positional handling).
    // Listing it as an end-alternative made parseBodyWithClauses chop every
    // clause containing a positional `last` at that token (ar focus-trap lost
    // its if-branch body). النهاية is what the i18n dict actually emits.
    end: { primary: 'نهاية', alternatives: ['انتهى', 'النهاية'], normalized: 'end' },
    // Advanced
    js: { primary: 'جافاسكربت', alternatives: ['js'], normalized: 'js' },
    async: { primary: 'متزامن', normalized: 'async' },
    tell: { primary: 'أخبر', normalized: 'tell' },
    default: { primary: 'افتراضي', normalized: 'default' },
    init: { primary: 'تهيئة', alternatives: ['بدء'], normalized: 'init' },
    behavior: { primary: 'سلوك', normalized: 'behavior' },
    install: { primary: 'تثبيت', alternatives: ['ثبّت'], normalized: 'install' },
    // `قِس` is the imperative with the kasra diacritic; the i18n dict (and real
    // Arabic prose) emits it undiacritized as `قس`. BOTH stay listed, and not
    // for the tokenizer's sake — keyword lookup is diacritic-insensitive now, so
    // either spelling resolves. It is the vocab gate's V1 check, which compares
    // the profile against the i18n DICTIONARY as strings: the dictionary says
    // `قس`, so dropping it here fails V1 (verified). Diacritic-insensitivity
    // would have to reach that comparison too before this pair can collapse.
    measure: { primary: 'قياس', alternatives: ['قِس', 'قس'], normalized: 'measure' },
    beep: { primary: 'صفّر', normalized: 'beep' },
    break: { primary: 'توقف', normalized: 'break' },
    copy: { primary: 'انسخ', normalized: 'copy' },
    exit: { primary: 'اخرج', normalized: 'exit' },
    pick: { primary: 'اختر', normalized: 'pick' },
    render: { primary: 'ارسم', normalized: 'render' },
    // Modifiers
    into: { primary: 'في', alternatives: ['إلى'], normalized: 'into' },
    before: { primary: 'قبل', normalized: 'before' },
    after: { primary: 'بعد', normalized: 'after' },
    // Event modifiers (for repeat until event)
    until: { primary: 'حتى', normalized: 'until' },
    event: { primary: 'حدث', normalized: 'event' },
    from: { primary: 'من', normalized: 'from' },
    // Phase 8 (htmx v4 localized attributes): attribute-suffix keywords
    // used by the vocab generator — `sse-اتصل`, `hx-مباشر`, etc.
    // Imperative form matching the profile's verb convention.
    connect: { primary: 'اتصل', alternatives: ['اتصال'], normalized: 'connect' },
    stream: { primary: 'تدفق', alternatives: ['بث'], normalized: 'stream' },
    live: { primary: 'مباشر', alternatives: ['حي'], normalized: 'live' },
    socket: { primary: 'مقبس', alternatives: ['websocket'], normalized: 'socket' },
    // Reactive / realtime commands
    bind: { primary: 'اربط', alternatives: ['ربط', 'bind'], normalized: 'bind' },
    intercept: { primary: 'اعترض', alternatives: ['اعتراض', 'intercept'], normalized: 'intercept' },
    worker: { primary: 'عامل', alternatives: ['worker'], normalized: 'worker' },
    eventsource: {
      primary: 'eventsource',
      alternatives: ['مصدر-الأحداث'],
      normalized: 'eventsource',
    },
  },
  tokenization: {
    prefixes: ['ال', 'و', 'ف', 'ب', 'ك', 'ل'],
  },
  eventHandler: {
    // Event marker: عند (at/upon), used in VSO pattern
    // Pattern: عند [event] [action] [patient] [على destination]
    // Example: عند النقر بدّل .active على #button
    // Includes temporal conjunctions (عندما = formal "when", حين = neutral, لمّا = dialectal)
    eventMarker: {
      primary: 'عند',
      alternatives: ['في', 'لدى', 'عندما', 'حين', 'حينما', 'لمّا', 'لما'],
      position: 'before',
    },
    temporalMarkers: ['عندما', 'حين', 'لمّا'], // kept for reference/documentation
    // Negation marker: عدم (not/lack of), used for negated events
    // Pattern: عند عدم [event] [action] [patient]
    // Example: عند عدم التركيز أخف #tooltip = "on blur hide #tooltip"
    negationMarker: { primary: 'عدم', alternatives: ['عدم'], position: 'before' },
  },
};
